import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './PrintCenterPage.css';
import {
    PrintDestinationDTO,
    PrintDestinationPayload,
    PrintTemplateDTO,
    PrintTemplatePayload,
    createPrintDestination,
    createPrintTemplate,
    deletePrintDestination,
    deletePrintTemplate,
    listPrintDestinations,
    listPrintTemplates,
    updatePrintDestination,
    updatePrintTemplate,
} from '../../api/printApi';

const DESTINATION_TYPES: { value: string; label: string }[] = [
    { value: 'FULL_ORDER', label: 'Riepilogo completo' },
    { value: 'KITCHEN', label: 'Cucina' },
    { value: 'BAR', label: 'Bar' },
    { value: 'RECEIPT', label: 'Ricevuta / accompagnamento' },
];

const TEMPLATE_TYPES: { value: string; label: string }[] = [
    { value: 'FULL_ORDER', label: 'Riepilogo completo' },
    { value: 'KITCHEN', label: 'Cucina' },
    { value: 'BAR', label: 'Bar' },
    { value: 'RECEIPT', label: 'Ricevuta / accompagnamento' },
];

const AVAILABLE_LOCALES = ['it-IT', 'en-GB', 'fr-FR', 'de-DE'];

const CUSTOM_TEMPLATE_ID = 'custom';

type TemplatePreset = {
    id: string;
    templateType: string;
    label: string;
    description: string;
    content: string;
    preview: string;
};

const TEMPLATE_PRESETS: TemplatePreset[] = [
    {
        id: 'full-order-standard',
        templateType: 'FULL_ORDER',
        label: 'Ricevuta fiscale standard',
        description: 'Layout completo per la stampa al punto cassa con dati cliente, ordine e pagamenti.',
        content: `
<div style="text-align:center">
    {{#if payload.tenant.business.brandName}}
        <strong>{{payload.tenant.business.brandName}}</strong><br/>
    {{else if payload.tenant.tenantName}}
        <strong>{{payload.tenant.tenantName}}</strong><br/>
    {{else}}
        <strong>{{meta.orderCode}}</strong><br/>
    {{/if}}
    {{#if payload.tenant.business.fullAddress}}
        {{payload.tenant.business.fullAddress}}<br/>
    {{/if}}
    {{#if payload.tenant.business.vatNumber}}
        P.IVA: {{payload.tenant.business.vatNumber}}<br/>
    {{/if}}
    {{#if payload.tenant.business.taxCode}}
        C.F.: {{payload.tenant.business.taxCode}}<br/>
    {{/if}}
</div>
<hr/>
<div>Ordine {{meta.orderCode}}</div>
{{#if payload.customer.name}}
    <div>Cliente: {{payload.customer.name}}</div>
{{/if}}
{{#if payload.customer.phone}}
    <div>Telefono: {{payload.customer.phone}}</div>
{{/if}}
{{#if payload.deliveryAddress}}
    <div>Indirizzo: {{payload.deliveryAddress.street}}, {{payload.deliveryAddress.city}} {{payload.deliveryAddress.postalCode}}</div>
{{/if}}
<hr/>
{{#each payload.items}}
    <div>{{quantity}} × {{productName}} — {{currency totalGross}} {{#if notes}}(Note: {{notes}}){{/if}}</div>
{{/each}}
<hr/>
<div>Totale imponibile: {{currency payload.totals.totalNet}}</div>
<div>Totale IVA: {{currency payload.totals.totalVat}}</div>
<div>Totale ordine: <strong>{{currency payload.totals.total}}</strong></div>
{{#if payload.payment}}
    <div>Pagamento: {{payload.payment.method}} ({{payload.payment.status}})</div>
{{/if}}
<hr/>
<div style="text-align:center">
    Grazie per aver scelto {{#if payload.tenant.business.brandName}}{{payload.tenant.business.brandName}}{{else}}{{payload.tenant.tenantName}}{{/if}}
</div>
`,
        preview: 'Pupi’s Club & Vino\nOrdine PEPSC14D52\nCliente: Mario Rossi\nTelefono: 3331234567\nIndirizzo: Via Roma 10, Milano 20100\n- 2 × Pizza Margherita — €18,00\n- 1 × Tiramisù — €4,50\nTotale imponibile: €18,00\nTotale IVA: €4,50\nTotale ordine: €22,50\nPagamento: CARTA (PAID)',
    },
    {
        id: 'receipt-rider-standard',
        templateType: 'RECEIPT',
        label: 'Ricevuta compatta termica (58mm)',
        description: 'Versione ottimizzata per rotoli termici da 58mm.',
        content: `
<div style="text-align:center;font-size:14px">
    {{#if payload.tenant.business.brandName}}
        <strong>{{payload.tenant.business.brandName}}</strong><br/>
    {{else}}
        <strong>{{payload.tenant.tenantName}}</strong><br/>
    {{/if}}
    {{#if payload.tenant.business.fullAddress}}
        {{payload.tenant.business.fullAddress}}<br/>
    {{/if}}
    {{#if payload.tenant.business.vatNumber}}
        P.IVA: {{payload.tenant.business.vatNumber}}<br/>
    {{/if}}
</div>
<hr/>
Ordine: {{meta.orderCode}}<br/>
{{#if payload.customer.name}}Cliente: {{payload.customer.name}}<br/>{{/if}}
{{#if payload.customer.phone}}Tel: {{payload.customer.phone}}<br/>{{/if}}
{{#if payload.deliveryAddress}}{{payload.deliveryAddress.street}}, {{payload.deliveryAddress.city}}{{/if}}
<hr/>
{{#each payload.items}}
    {{quantity}} × {{productName}} - {{currency totalGross}}<br/>
{{/each}}
<hr/>
Totale: <strong>{{currency payload.totals.total}}</strong><br/>
{{#if payload.payment}}Pagamento: {{payload.payment.method}} ({{payload.payment.status}}){{/if}}
<hr/>
<div style="text-align:center">
    Grazie per aver scelto {{#if payload.tenant.business.brandName}}{{payload.tenant.business.brandName}}{{else}}{{payload.tenant.tenantName}}{{/if}}
</div>
`,
        preview: 'Pupi’s Club & Vino\nOrdine PEPSC14D52\nCliente: Mario Rossi\nTel: 3331234567\nVia Roma 10, Milano\n- 2 × Pizza Margherita - €18,00\n- 1 × Tiramisù - €4,50\nTotale: €22,50\nPagamento: CARTA (PAID)',
    },
    {
        id: 'kitchen-standard',
        templateType: 'KITCHEN',
        label: 'Comanda cucina compatta',
        description: 'Lista ordinata per la brigata con quantità e note evidenziate.',
        content: `
<div style="text-align:center">
    <strong>Comanda cucina</strong><br/>
    {{#if payload.tableNumber}}Tavolo {{payload.tableNumber}}{{else}}Ordine {{meta.orderCode}}{{/if}}
</div>
<hr/>
{{#if payload.destinationItems}}
    {{#each payload.destinationItems}}
        <div style="margin-bottom:10px">
            <strong>{{quantity}} × {{productName}}</strong><br/>
            {{#if variantName}}Variante: {{variantName}}<br/>{{/if}}
            {{#if notes}}<span style="font-size:0.85rem">Note: {{notes}}</span><br/>{{/if}}
        </div>
    {{/each}}
{{else}}
    {{#each payload.items}}
        <div style="margin-bottom:10px">
            <strong>{{quantity}} × {{productName}}</strong><br/>
            {{#if variantName}}Variante: {{variantName}}<br/>{{/if}}
            {{#if notes}}<span style="font-size:0.85rem">Note: {{notes}}</span><br/>{{/if}}
        </div>
    {{/each}}
{{/if}}
`,
        preview: 'Comanda cucina\nOrdine PEPSC14D52\n- 2 × Pizza Margherita\n- 1 × Tiramisù (Note: Poco cacao)',
    },
    {
        id: 'bar-standard',
        templateType: 'BAR',
        label: 'Comanda bar',
        description: 'Formato rapido per le postazioni bar o bevande.',
        content: `
<div style="text-align:center">
    <strong>Comanda bar</strong><br/>
    {{#if payload.tableNumber}}Tavolo {{payload.tableNumber}}{{else}}Ordine {{meta.orderCode}}{{/if}}
</div>
<hr/>
{{#if payload.destinationItems}}
    {{#each payload.destinationItems}}
        <div style="margin-bottom:8px">
            <strong>{{quantity}} × {{productName}}</strong>
            {{#if notes}}<div style="font-size:0.85rem">Note: {{notes}}</div>{{/if}}
        </div>
    {{/each}}
{{else}}
    {{#each payload.items}}
        <div style="margin-bottom:8px">
            <strong>{{quantity}} × {{productName}}</strong>
            {{#if notes}}<div style="font-size:0.85rem">Note: {{notes}}</div>{{/if}}
        </div>
    {{/each}}
{{/if}}
`,
        preview: 'Comanda bar\nOrdine PEPSC14D52\n- 2 × Negroni\n- 1 × Americano (Note: Poco ghiaccio)',
    },
    {
        id: 'receipt-detailed-logo',
        templateType: 'RECEIPT',
        label: 'Ricevuta dettagliata con logo',
        description: 'Include logo, indirizzo completo e note ordine.',
        content: `
<div style="text-align:center">
    {{#if branding.logoUrl}}
        <img src="{{branding.logoUrl}}" style="max-width:140px;margin-bottom:8px"/><br/>
    {{/if}}
    {{#if payload.tenant.business.brandName}}
        <strong>{{payload.tenant.business.brandName}}</strong><br/>
    {{else}}
        <strong>{{payload.tenant.tenantName}}</strong><br/>
    {{/if}}
    {{#if payload.tenant.business.fullAddress}}
        {{payload.tenant.business.fullAddress}}<br/>
    {{/if}}
    {{#if payload.tenant.business.vatNumber}}P.IVA: {{payload.tenant.business.vatNumber}}<br/>{{/if}}
    {{#if payload.tenant.business.taxCode}}C.F.: {{payload.tenant.business.taxCode}}<br/>{{/if}}
</div>
<hr/>
<div><strong>Ordine:</strong> {{meta.orderCode}}</div>
{{#if payload.customer.name}}<div><strong>Cliente:</strong> {{payload.customer.name}}</div>{{/if}}
{{#if payload.customer.phone}}<div><strong>Telefono:</strong> {{payload.customer.phone}}</div>{{/if}}
{{#if payload.deliveryAddress}}<div><strong>Indirizzo:</strong> {{payload.deliveryAddress.street}}, {{payload.deliveryAddress.city}} {{payload.deliveryAddress.postalCode}}</div>{{/if}}
<hr/>
{{#each payload.items}}
    <div>
        {{quantity}} × {{productName}} — {{currency totalGross}}
        {{#if notes}}<em style="display:block;font-size:0.85rem">Note: {{notes}}</em>{{/if}}
    </div>
{{/each}}
<hr/>
<div><strong>Totale imponibile:</strong> {{currency payload.totals.totalNet}}</div>
<div><strong>Totale IVA:</strong> {{currency payload.totals.totalVat}}</div>
<div><strong>Totale ordine:</strong> {{currency payload.totals.total}}</div>
{{#if payload.payment}}
    <div><strong>Pagamento:</strong> {{payload.payment.method}} ({{payload.payment.status}})</div>
{{/if}}
<hr/>
<div style="text-align:center">
    Grazie per aver scelto {{#if payload.tenant.business.brandName}}{{payload.tenant.business.brandName}}{{else}}{{payload.tenant.tenantName}}{{/if}}
</div>
`,
        preview: 'Ricevuta con logo\nOrdine PEPSC14D52\nCliente: Mario Rossi\nTelefono: 3331234567\n- 2 × Pizza Margherita — €18,00\n- 1 × Tiramisù — €4,50\nTotale imponibile: €18,00\nTotale IVA: €4,50\nTotale ordine: €22,50\nPagamento: CARTA (PAID)',
    },
];

const TEMPLATE_PRESETS_BY_TYPE = TEMPLATE_PRESETS.reduce<Record<string, TemplatePreset[]>>((acc, preset) => {
    if (!acc[preset.templateType]) {
        acc[preset.templateType] = [];
    }
    acc[preset.templateType].push(preset);
    return acc;
}, {});

type DestinationFormState = {
    id?: number;
    code: string;
    name: string;
    destinationType: string;
    connectionType: string;
    rawConfig: string;
    enabled: boolean;
    isDefault: boolean;
    submitting: boolean;
    error: string | null;
    success: string | null;
};

type TemplateFormState = {
    id?: number;
    code: string;
    name: string;
    description: string;
    templateType: string;
    locale: string;
    payloadSchemaVersion: number;
    content: string;
    footer: string;
    active: boolean;
    presetId: string;
    showAdvanced: boolean;
    submitting: boolean;
    error: string | null;
    success: string | null;
};

const blankDestinationForm: DestinationFormState = {
    code: '',
    name: '',
    destinationType: 'FULL_ORDER',
    connectionType: '',
    rawConfig: '',
    enabled: true,
    isDefault: false,
    submitting: false,
    error: null,
    success: null,
};

function createBlankTemplateForm(templateType: string = 'FULL_ORDER'): TemplateFormState {
    const presets = TEMPLATE_PRESETS_BY_TYPE[templateType] ?? [];
    const defaultPreset = presets.length > 0 ? presets[0] : undefined;
    const hasPreset = Boolean(defaultPreset);
    return {
        code: '',
        name: defaultPreset ? defaultPreset.label : '',
        description: defaultPreset ? defaultPreset.description : '',
        templateType,
        locale: 'it-IT',
        payloadSchemaVersion: 1,
        content: defaultPreset ? defaultPreset.content : '',
        footer: '',
        active: true,
        presetId: defaultPreset ? defaultPreset.id : CUSTOM_TEMPLATE_ID,
        showAdvanced: !hasPreset,
        submitting: false,
        error: null,
        success: null,
    };
}

function detectPresetId(templateType: string, content: string): string {
    const presets = TEMPLATE_PRESETS_BY_TYPE[templateType] ?? [];
    const normalized = content.trim();
    const match = presets.find(preset => preset.content.trim() === normalized);
    return match ? match.id : CUSTOM_TEMPLATE_ID;
}

function getPresetById(templateType: string, presetId: string): TemplatePreset | undefined {
    return (TEMPLATE_PRESETS_BY_TYPE[templateType] ?? []).find(preset => preset.id === presetId);
}

function normaliseJson(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    try {
        return JSON.stringify(value, null, 2);
    } catch (err) {
        return String(value);
    }
}

const PrintCenterPage: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [pageError, setPageError] = useState<string | null>(null);
    const [destinations, setDestinations] = useState<PrintDestinationDTO[]>([]);
    const [templates, setTemplates] = useState<PrintTemplateDTO[]>([]);
    const [destinationForm, setDestinationForm] = useState<DestinationFormState>(blankDestinationForm);
    const [templateForm, setTemplateForm] = useState<TemplateFormState>(() => createBlankTemplateForm());
    const [searchParams] = useSearchParams();
    const [highlightTemplateId, setHighlightTemplateId] = useState<number | null>(null);
    const [highlightDestinationId, setHighlightDestinationId] = useState<number | null>(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setPageError(null);
        try {
            const [destList, templateList] = await Promise.all([
                listPrintDestinations(),
                listPrintTemplates(),
            ]);
            setDestinations(destList);
            setTemplates(templateList);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Errore imprevisto nel caricamento';
            setPageError(message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleResetDestinationForm = () => {
        setDestinationForm({ ...blankDestinationForm });
    };

    const handleResetTemplateForm = () => {
        setTemplateForm(prev => createBlankTemplateForm(prev.templateType));
    };

    const handleEditDestination = useCallback((destination: PrintDestinationDTO) => {
        setDestinationForm({
            id: destination.id,
            code: destination.code,
            name: destination.name,
            destinationType: destination.destinationType,
            connectionType: destination.connectionType ?? '',
            rawConfig: normaliseJson(destination.connectionConfig),
            enabled: destination.enabled,
            isDefault: destination.isDefault,
            submitting: false,
            error: null,
            success: null,
        });
    }, []);

    const handleEditTemplate = useCallback((template: PrintTemplateDTO) => {
        const presetId = detectPresetId(template.templateType, template.content ?? '');
        setTemplateForm({
            id: template.id,
            code: template.code,
            name: template.name,
            description: template.description ?? '',
            templateType: template.templateType,
            locale: template.locale,
            payloadSchemaVersion: template.payloadSchemaVersion ?? 1,
            content: template.content ?? '',
            footer: template.footer ?? '',
            active: template.active,
            presetId,
            showAdvanced: presetId === CUSTOM_TEMPLATE_ID,
            submitting: false,
            error: null,
            success: null,
        });
    }, []);

    const scrollToElement = useCallback((elementId: string) => {
        if (typeof document === 'undefined') {
            return;
        }
        const element = document.getElementById(elementId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, []);

    useEffect(() => {
        const focus = searchParams.get('focus');
        const templateIdParam = searchParams.get('templateId');
        const templateTypeParam = searchParams.get('templateType');
        const destinationIdParam = searchParams.get('destinationId');
        const destinationTypeParam = searchParams.get('destinationType');

        if (templateIdParam && templates.length > 0) {
            const match = templates.find(template => String(template.id) === templateIdParam);
            if (match) {
                handleEditTemplate(match);
                if (match.id != null) {
                    setHighlightTemplateId(match.id);
                    scrollToElement(`template-${match.id}`);
                }
            }
        } else if (focus === 'template' && templateTypeParam) {
            const exists = TEMPLATE_TYPES.some(type => type.value === templateTypeParam);
            if (exists) {
                setTemplateForm(prev => {
                    if (prev.id) {
                        return prev;
                    }
                    if (prev.templateType === templateTypeParam) {
                        return prev;
                    }
                    return { ...createBlankTemplateForm(templateTypeParam) };
                });
            }
        }

        if (destinationIdParam && destinations.length > 0) {
            const match = destinations.find(destination => String(destination.id) === destinationIdParam);
            if (match) {
                handleEditDestination(match);
                if (match.id != null) {
                    setHighlightDestinationId(match.id);
                    scrollToElement(`destination-${match.id}`);
                }
            }
        } else if (focus === 'destination' && destinationTypeParam) {
            const exists = DESTINATION_TYPES.some(type => type.value === destinationTypeParam);
            if (exists) {
                setDestinationForm(prev => {
                    if (prev.id) {
                        return prev;
                    }
                    if (prev.destinationType === destinationTypeParam) {
                        return prev;
                    }
                    return {
                        ...blankDestinationForm,
                        destinationType: destinationTypeParam,
                    };
                });
            }
        }
    }, [searchParams, templates, destinations, handleEditTemplate, handleEditDestination, scrollToElement]);

    useEffect(() => {
        if (highlightTemplateId === null) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }
        const timer = window.setTimeout(() => setHighlightTemplateId(null), 4000);
        return () => window.clearTimeout(timer);
    }, [highlightTemplateId]);

    useEffect(() => {
        if (highlightDestinationId === null) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }
        const timer = window.setTimeout(() => setHighlightDestinationId(null), 4000);
        return () => window.clearTimeout(timer);
    }, [highlightDestinationId]);

    const handleDeleteDestination = async (destination: PrintDestinationDTO) => {
        const confirmed = window.confirm(`Eliminare la stampante "${destination.name}"?`);
        if (!confirmed) {
            return;
        }
        try {
            await deletePrintDestination(destination.id);
            await loadData();
            if (destinationForm.id === destination.id) {
                handleResetDestinationForm();
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile eliminare la stampante';
            alert(message);
        }
    };

    const handleDeleteTemplate = async (template: PrintTemplateDTO) => {
        const confirmed = window.confirm(`Eliminare il template "${template.name}"?`);
        if (!confirmed) {
            return;
        }
        try {
            await deletePrintTemplate(template.id);
            await loadData();
            if (templateForm.id === template.id) {
                handleResetTemplateForm();
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile eliminare il template';
            alert(message);
        }
    };

    const handleTemplateTypeChange = useCallback((nextType: string) => {
        setTemplateForm(prev => {
            if (prev.templateType === nextType) {
                return prev;
            }
            if (prev.showAdvanced) {
                return {
                    ...prev,
                    templateType: nextType,
                    presetId: CUSTOM_TEMPLATE_ID,
                };
            }
            const presets = TEMPLATE_PRESETS_BY_TYPE[nextType] ?? [];
            if (presets.length === 0) {
                return {
                    ...prev,
                    templateType: nextType,
                    presetId: CUSTOM_TEMPLATE_ID,
                    showAdvanced: true,
                };
            }
            const candidate = presets.find(preset => preset.id === prev.presetId) ?? presets[0];
            const previousPreset = getPresetById(prev.templateType, prev.presetId ?? '');
            const nameTrimmed = prev.name.trim();
            const descriptionTrimmed = prev.description.trim();
            const shouldUpdateName = !prev.id && (nameTrimmed.length === 0 || (previousPreset && nameTrimmed === previousPreset.label));
            const shouldUpdateDescription = !prev.id && (descriptionTrimmed.length === 0 || (previousPreset && descriptionTrimmed === previousPreset.description));
            return {
                ...prev,
                templateType: nextType,
                presetId: candidate.id,
                content: candidate.content,
                showAdvanced: false,
                name: shouldUpdateName ? candidate.label : prev.name,
                description: shouldUpdateDescription ? candidate.description : prev.description,
            };
        });
    }, []);

    const handleTemplatePresetChange = useCallback((presetId: string) => {
        if (presetId === CUSTOM_TEMPLATE_ID) {
            setTemplateForm(prev => ({
                ...prev,
                presetId: CUSTOM_TEMPLATE_ID,
                showAdvanced: true,
            }));
            return;
        }
        setTemplateForm(prev => {
            const preset = getPresetById(prev.templateType, presetId);
            if (!preset) {
                return prev;
            }
            const previousPreset = getPresetById(prev.templateType, prev.presetId ?? '');
            const nameTrimmed = prev.name.trim();
            const descriptionTrimmed = prev.description.trim();
            const shouldUpdateName = !prev.id && (nameTrimmed.length === 0 || (previousPreset && nameTrimmed === previousPreset.label));
            const shouldUpdateDescription = !prev.id && (descriptionTrimmed.length === 0 || (previousPreset && descriptionTrimmed === previousPreset.description));
            return {
                ...prev,
                presetId: preset.id,
                content: preset.content,
                showAdvanced: false,
                name: shouldUpdateName ? preset.label : prev.name,
                description: shouldUpdateDescription ? preset.description : prev.description,
            };
        });
    }, []);

    const toggleAdvancedEditing = useCallback(() => {
        setTemplateForm(prev => {
            if (prev.showAdvanced) {
                const presets = TEMPLATE_PRESETS_BY_TYPE[prev.templateType] ?? [];
                if (presets.length === 0) {
                    return {
                        ...prev,
                        showAdvanced: false,
                        presetId: CUSTOM_TEMPLATE_ID,
                    };
                }
                const fallback = presets.find(preset => preset.id === prev.presetId) ?? presets[0];
                return {
                    ...prev,
                    showAdvanced: false,
                    presetId: fallback.id,
                    content: fallback.content,
                };
            }
            const presets = TEMPLATE_PRESETS_BY_TYPE[prev.templateType] ?? [];
            if (presets.length === 0) {
                return {
                    ...prev,
                    showAdvanced: true,
                    presetId: CUSTOM_TEMPLATE_ID,
                };
            }
            const currentPreset = presets.find(preset => preset.id === prev.presetId) ?? presets[0];
            return {
                ...prev,
                showAdvanced: true,
                presetId: CUSTOM_TEMPLATE_ID,
                content: prev.content || currentPreset.content,
            };
        });
    }, []);

    const submitDestination = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setDestinationForm(prev => ({ ...prev, submitting: true, error: null, success: null }));

        if (!destinationForm.code.trim() || !destinationForm.name.trim()) {
            setDestinationForm(prev => ({
                ...prev,
                submitting: false,
                error: 'Codice e nome sono obbligatori.',
            }));
            return;
        }

        let connectionConfig: unknown = undefined;
        if (destinationForm.rawConfig.trim()) {
            try {
                connectionConfig = JSON.parse(destinationForm.rawConfig);
            } catch (err) {
                setDestinationForm(prev => ({
                    ...prev,
                    submitting: false,
                    error: 'Configurazione connessione non valida: inserire JSON valido.',
                }));
                return;
            }
        }

        const payload: PrintDestinationPayload = {
            code: destinationForm.code.trim(),
            name: destinationForm.name.trim(),
            destinationType: destinationForm.destinationType,
            connectionType: destinationForm.connectionType.trim() || undefined,
            connectionConfig,
            enabled: destinationForm.enabled,
            isDefault: destinationForm.isDefault,
        };

        try {
            if (destinationForm.id) {
                await updatePrintDestination(destinationForm.id, payload);
            } else {
                await createPrintDestination(payload);
            }
            await loadData();
            setDestinationForm(prev => ({
                ...blankDestinationForm,
                success: prev.id ? 'Stampante aggiornata.' : 'Stampante creata.',
            }));
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Errore durante il salvataggio.';
            setDestinationForm(prev => ({
                ...prev,
                submitting: false,
                error: message,
            }));
        }
    };

    const submitTemplate = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setTemplateForm(prev => ({ ...prev, submitting: true, error: null, success: null }));

        if (!templateForm.code.trim() || !templateForm.name.trim() || !templateForm.content.trim()) {
            setTemplateForm(prev => ({
                ...prev,
                submitting: false,
                error: 'Codice, nome e contenuto sono obbligatori.',
            }));
            return;
        }

        const payload: PrintTemplatePayload = {
            code: templateForm.code.trim(),
            name: templateForm.name.trim(),
            description: templateForm.description.trim() || undefined,
            templateType: templateForm.templateType,
            locale: templateForm.locale.trim(),
            payloadSchemaVersion: templateForm.payloadSchemaVersion || 1,
            content: templateForm.content,
            footer: templateForm.footer.trim() || undefined,
            active: templateForm.active,
        };

        try {
            if (templateForm.id) {
                await updatePrintTemplate(templateForm.id, payload);
            } else {
                await createPrintTemplate(payload);
            }
            await loadData();
            setTemplateForm(prev => ({
                ...createBlankTemplateForm(prev.templateType),
                success: prev.id ? 'Template aggiornato.' : 'Template creato.',
            }));
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Errore durante il salvataggio del template.';
            setTemplateForm(prev => ({
                ...prev,
                submitting: false,
                error: message,
            }));
        }
    };

    const availablePresets = useMemo(() => {
        return TEMPLATE_PRESETS_BY_TYPE[templateForm.templateType] ?? [];
    }, [templateForm.templateType]);

    const selectedPreset = useMemo(() => {
        if (templateForm.presetId === CUSTOM_TEMPLATE_ID) {
            return null;
        }
        return availablePresets.find(preset => preset.id === templateForm.presetId) ?? null;
    }, [availablePresets, templateForm.presetId]);

    const destinationsByType = useMemo(() => {
        return destinations.reduce<Record<string, PrintDestinationDTO[]>>((acc, destination) => {
            const key = destination.destinationType ?? 'UNKNOWN';
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(destination);
            return acc;
        }, {});
    }, [destinations]);

    const templatesByType = useMemo(() => {
        return templates.reduce<Record<string, PrintTemplateDTO[]>>((acc, template) => {
            const key = template.templateType ?? 'UNKNOWN';
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(template);
            return acc;
        }, {});
    }, [templates]);

    return (
        <div className="print-center-page">
            <header className="print-center-header">
                <div>
                    <h1>Centro stampa</h1>
                    <p>Configura le stampanti del tuo locale e definisci i template che verranno utilizzati per le stampe automatiche.</p>
                </div>
                <button type="button" className="ghost" onClick={loadData} disabled={loading}>
                    {loading ? 'Caricamento…' : 'Ricarica dati'}
                </button>
            </header>

            {pageError && (
                <div className="alert error">{pageError}</div>
            )}

            <section className="print-section">
                <div className="section-header">
                    <div>
                        <h2>Stampanti collegate</h2>
                        <p>Registra ogni stampante in uso (cassa, cucina, bar). Il software locale userà il codice per collegarsi.</p>
                    </div>
                    <button type="button" onClick={handleResetDestinationForm} className="link">
                        {destinationForm.id ? 'Nuova stampante' : 'Reset form'}
                    </button>
                </div>

                <div className="section-grid">
                    <div className="section-list">
                        {loading ? (
                            <p>Caricamento stampanti…</p>
                        ) : destinations.length === 0 ? (
                            <p className="empty">Nessuna stampante configurata.</p>
                        ) : (
                            <ul>
                                {destinations.map(destination => (
                                    <li
                                        key={destination.id}
                                        id={destination.id != null ? `destination-${destination.id}` : undefined}
                                        className={highlightDestinationId === destination.id ? 'highlighted' : undefined}
                                    >
                                        <div className="list-title">
                                            <strong>{destination.name}</strong>
                                            <span>{destination.destinationType}</span>
                                        </div>
                                        <div className="list-meta">
                                            <span>Codice: {destination.code}</span>
                                            {destination.connectionType && <span>Connessione: {destination.connectionType}</span>}
                                            <span>{destination.enabled ? 'Attiva' : 'Disabilitata'}</span>
                                            {destination.isDefault && <span>Default</span>}
                                        </div>
                                        <div className="list-actions">
                                            <button type="button" onClick={() => handleEditDestination(destination)}>Modifica</button>
                                            <button type="button" className="danger" onClick={() => handleDeleteDestination(destination)}>Elimina</button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <form className="section-form" onSubmit={submitDestination}>
                        <h3>{destinationForm.id ? 'Modifica stampante' : 'Nuova stampante'}</h3>
                        {destinationForm.error && <div className="alert error">{destinationForm.error}</div>}
                        {destinationForm.success && <div className="alert success">{destinationForm.success}</div>}
                        <label>
                            Codice identificativo
                            <input
                                type="text"
                                value={destinationForm.code}
                                onChange={event => setDestinationForm(prev => ({ ...prev, code: event.target.value }))}
                                placeholder="es. cucina-main"
                                required
                            />
                        </label>
                        <label>
                            Nome visualizzato
                            <input
                                type="text"
                                value={destinationForm.name}
                                onChange={event => setDestinationForm(prev => ({ ...prev, name: event.target.value }))}
                                placeholder="Stampante cucina"
                                required
                            />
                        </label>
                        <label>
                            Tipo di destinazione
                            <select
                                value={destinationForm.destinationType}
                                onChange={event => setDestinationForm(prev => ({ ...prev, destinationType: event.target.value }))}
                            >
                                {DESTINATION_TYPES.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Tipo di connessione (USB, LAN, Wi-Fi…)
                            <input
                                type="text"
                                value={destinationForm.connectionType}
                                onChange={event => setDestinationForm(prev => ({ ...prev, connectionType: event.target.value }))}
                                placeholder="USB"
                            />
                        </label>
                        <label>
                            Configurazione connessione (JSON opzionale)
                            <textarea
                                rows={5}
                                value={destinationForm.rawConfig}
                                onChange={event => setDestinationForm(prev => ({ ...prev, rawConfig: event.target.value }))}
                                placeholder='{"vendor":"bixolon","device":"COM3"}'
                            />
                        </label>
                        <div className="form-inline">
                            <label className="checkbox">
                                <input
                                    type="checkbox"
                                    checked={destinationForm.enabled}
                                    onChange={event => setDestinationForm(prev => ({ ...prev, enabled: event.target.checked }))}
                                />
                                Abilitata
                            </label>
                            <label className="checkbox">
                                <input
                                    type="checkbox"
                                    checked={destinationForm.isDefault}
                                    onChange={event => setDestinationForm(prev => ({ ...prev, isDefault: event.target.checked }))}
                                />
                                Predefinita
                            </label>
                        </div>
                        <button type="submit" disabled={destinationForm.submitting}>
                            {destinationForm.submitting ? 'Salvataggio…' : destinationForm.id ? 'Aggiorna stampante' : 'Crea stampante'}
                        </button>
                    </form>
                </div>
            </section>

            <section className="print-section">
                <div className="section-header">
                    <div>
                        <h2>Template di stampa</h2>
                        <p>Scegli un modello precompilato oppure personalizza i testi stampati per ogni tipologia.</p>
                    </div>
                    <button type="button" onClick={handleResetTemplateForm} className="link">
                        {templateForm.id ? 'Nuovo template' : 'Reset form'}
                    </button>
                </div>

                <div className="section-grid">
                    <div className="section-list">
                        {loading ? (
                            <p>Caricamento template…</p>
                        ) : templates.length === 0 ? (
                            <p className="empty">Nessun template di stampa configurato.</p>
                        ) : (
                            <ul>
                                {templates.map(template => (
                                    <li
                                        key={template.id}
                                        id={template.id != null ? `template-${template.id}` : undefined}
                                        className={highlightTemplateId === template.id ? 'highlighted' : undefined}
                                    >
                                        <div className="list-title">
                                            <strong>{template.name}</strong>
                                            <span>{template.templateType}</span>
                                        </div>
                                        <div className="list-meta">
                                            <span>Codice: {template.code}</span>
                                            <span>Locale: {template.locale}</span>
                                            <span>{template.active ? 'Attivo' : 'Disabilitato'}</span>
                                        </div>
                                        <div className="list-actions">
                                            <button type="button" onClick={() => handleEditTemplate(template)}>Modifica</button>
                                            <button type="button" className="danger" onClick={() => handleDeleteTemplate(template)}>Elimina</button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <form className="section-form" onSubmit={submitTemplate}>
                        <h3>{templateForm.id ? 'Modifica template' : 'Nuovo template'}</h3>
                        {templateForm.error && <div className="alert error">{templateForm.error}</div>}
                        {templateForm.success && <div className="alert success">{templateForm.success}</div>}
                        <label>
                            Codice
                            <input
                                type="text"
                                value={templateForm.code}
                                onChange={event => setTemplateForm(prev => ({ ...prev, code: event.target.value }))}
                                placeholder="es. full-order-it"
                                required
                            />
                        </label>
                        <label>
                            Nome
                            <input
                                type="text"
                                value={templateForm.name}
                                onChange={event => setTemplateForm(prev => ({ ...prev, name: event.target.value }))}
                                placeholder="Riepilogo completo (IT)"
                                required
                            />
                        </label>
                        <label>
                            Descrizione
                            <input
                                type="text"
                                value={templateForm.description}
                                onChange={event => setTemplateForm(prev => ({ ...prev, description: event.target.value }))}
                                placeholder="Descrizione interna"
                            />
                        </label>
                        <label>
                            Tipo di template
                            <select
                                value={templateForm.templateType}
                                onChange={event => handleTemplateTypeChange(event.target.value)}
                            >
                                {TEMPLATE_TYPES.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Layout predefinito
                            <select
                                value={templateForm.presetId}
                                onChange={event => handleTemplatePresetChange(event.target.value)}
                            >
                                {availablePresets.map(preset => (
                                    <option key={preset.id} value={preset.id}>{preset.label}</option>
                                ))}
                                <option value={CUSTOM_TEMPLATE_ID}>Personalizzato</option>
                            </select>
                            <small className="help-text">
                                I testi sono già pronti: scegli un layout oppure passa alla modalità personalizzata per modificarli.
                            </small>
                        </label>
                        {selectedPreset && !templateForm.showAdvanced && (
                            <div className="template-preview-card">
                                <strong>Anteprima testo</strong>
                                <pre style={{ whiteSpace: 'pre-wrap' }}>{selectedPreset.preview}</pre>
                                <small className="help-text">In stampa i valori saranno sostituiti con i dati reali dell'ordine.</small>
                            </div>
                        )}
                        {availablePresets.length > 0 && (
                            <div className="template-advanced-toggle">
                                <button
                                    type="button"
                                    className="link"
                                    onClick={toggleAdvancedEditing}
                                >
                                    {templateForm.showAdvanced ? 'Nascondi contenuto avanzato' : 'Personalizza testo avanzato'}
                                </button>
                            </div>
                        )}
                        <label>
                            Lingua
                            <input
                                list="print-template-locales"
                                value={templateForm.locale}
                                onChange={event => setTemplateForm(prev => ({ ...prev, locale: event.target.value }))}
                                placeholder="it-IT"
                                required
                            />
                            <datalist id="print-template-locales">
                                {AVAILABLE_LOCALES.map(locale => (
                                    <option key={locale} value={locale} />
                                ))}
                            </datalist>
                        </label>
                        <label>
                            Versione schema payload
                            <input
                                type="number"
                                min={1}
                                value={templateForm.payloadSchemaVersion}
                                onChange={event => setTemplateForm(prev => ({ ...prev, payloadSchemaVersion: Number(event.target.value) || 1 }))}
                            />
                        </label>
                        {(templateForm.showAdvanced || availablePresets.length === 0) && (
                            <label>
                                Contenuto template (avanzato)
                                <textarea
                                    rows={8}
                                    value={templateForm.content}
                                    onChange={event => setTemplateForm(prev => ({ ...prev, content: event.target.value }))}
                                    placeholder="Intestazione locale\nOrdine #A123"
                                    required
                                />
                                <small className="help-text">
                                    Sono ammessi segnaposto come <code>{'{{orderCode}}'}</code> per inserire i dati dinamici.
                                </small>
                            </label>
                        )}
                        <label>
                            Footer opzionale
                            <textarea
                                rows={3}
                                value={templateForm.footer}
                                onChange={event => setTemplateForm(prev => ({ ...prev, footer: event.target.value }))}
                                placeholder="Grazie per averci scelto!"
                            />
                        </label>
                        <label className="checkbox">
                            <input
                                type="checkbox"
                                checked={templateForm.active}
                                onChange={event => setTemplateForm(prev => ({ ...prev, active: event.target.checked }))}
                            />
                            Template attivo
                        </label>
                        <button type="submit" disabled={templateForm.submitting}>
                            {templateForm.submitting ? 'Salvataggio…' : templateForm.id ? 'Aggiorna template' : 'Crea template'}
                        </button>
                    </form>
                </div>
            </section>

            <section className="print-summary">
                <h2>Riepilogo configurazione</h2>
                <div className="summary-grid">
                    {DESTINATION_TYPES.map(type => {
                        const list = destinationsByType[type.value] ?? [];
                        return (
                            <article key={type.value}>
                                <header>
                                    <h3>{type.label}</h3>
                                    <span>{list.length} stampante{list.length === 1 ? '' : 'i'}</span>
                                </header>
                                {list.length === 0 ? (
                                    <p className="empty">Nessuna stampante associata.</p>
                                ) : (
                                    <ul>
                                        {list.map(item => (
                                            <li key={item.id}>{item.name} — {item.code}</li>
                                        ))}
                                    </ul>
                                )}
                            </article>
                        );
                    })}
                    {TEMPLATE_TYPES.map(type => {
                        const list = templatesByType[type.value] ?? [];
                        return (
                            <article key={`${type.value}-template`}>
                                <header>
                                    <h3>{type.label} (template)</h3>
                                    <span>{list.length} template</span>
                                </header>
                                {list.length === 0 ? (
                                    <p className="empty">Nessun template configurato.</p>
                                ) : (
                                    <ul>
                                        {list.map(item => (
                                            <li key={item.id}>{item.name} — {item.locale}</li>
                                        ))}
                                    </ul>
                                )}
                            </article>
                        );
                    })}
                </div>
            </section>
        </div>
    );
};

export default PrintCenterPage;
